import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ShoppingCart, Sparkles } from "lucide-react";
import { useSession } from "@/hooks/use-session";
import { supabase } from "@/lib/supabase";
import { SkeletonLoader, EmptyState } from "@/components/system";
import { formatMoney } from "@/lib/currency";

type Item = {
  id: string;
  raw_text: string;
  quantity: number | null;
  unit: string | null;
  category: string | null;
  preferred_store: string | null;
  estimated_cost: number | null;
  estimated_cost_currency: string | null;
  done: boolean;
  added_by: string;
};

const CATEGORY_ORDER = ["produce", "dairy", "meat", "frozen", "dry", "household", "other"];

export function ShoppingList() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const queryClient = useQueryClient();
  const [generating, setGenerating] = useState(false);

  const list = useQuery({
    queryKey: ["shopping_list", userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      const { data, error } = await (supabase as never as { from: (t: string) => any })
        .from("shopping_list")
        .select("id, name, archived_at")
        .eq("user_id", userId!)
        .is("archived_at", null)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data as { id: string; name: string } | null;
    },
  });

  const items = useQuery({
    queryKey: ["shopping_list_items", list.data?.id],
    enabled: Boolean(list.data?.id),
    queryFn: async () => {
      const { data, error } = await (supabase as never as { from: (t: string) => any })
        .from("shopping_list_item")
        .select("*")
        .eq("shopping_list_id", list.data!.id)
        .order("done")
        .order("category");
      if (error) throw error;
      return (data ?? []) as Item[];
    },
  });

  const toggleItem = useMutation({
    mutationFn: async ({ id, done }: { id: string; done: boolean }) => {
      const { error } = await (supabase as never as { from: (t: string) => any })
        .from("shopping_list_item")
        .update({ done, done_at: done ? new Date().toISOString() : null })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["shopping_list_items"] }),
  });

  const addItem = useMutation({
    mutationFn: async (raw_text: string) => {
      let listId = list.data?.id;
      if (!listId) {
        const { data, error } = await (supabase as never as { from: (t: string) => any })
          .from("shopping_list")
          .insert({ user_id: userId, name: "This week" })
          .select("id")
          .single();
        if (error) throw error;
        listId = (data as { id: string }).id;
      }
      const { error } = await (supabase as never as { from: (t: string) => any })
        .from("shopping_list_item")
        .insert({ shopping_list_id: listId, raw_text, added_by: "manual" });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shopping_list", userId] });
      queryClient.invalidateQueries({ queryKey: ["shopping_list_items"] });
    },
  });

  async function generateFromMealPlan() {
    setGenerating(true);
    try {
      const res = await fetch("/api/shopping/generate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
      });
      if (!res.ok) {
        alert("Generation failed: " + (await res.text()));
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["shopping_list", userId] });
      queryClient.invalidateQueries({ queryKey: ["shopping_list_items"] });
    } finally {
      setGenerating(false);
    }
  }

  const [adding, setAdding] = useState("");

  const grouped = new Map<string, Item[]>();
  for (const i of items.data ?? []) {
    const cat = i.category ?? "other";
    if (!grouped.has(cat)) grouped.set(cat, []);
    grouped.get(cat)!.push(i);
  }
  const sortedCats = [...grouped.keys()].sort(
    (a, b) => CATEGORY_ORDER.indexOf(a) - CATEGORY_ORDER.indexOf(b),
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-5)" }}>
      <header style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <ShoppingCart size={22} color="var(--accent)" />
        <h1 style={{ margin: 0, fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: 28 }}>Shopping</h1>
        <div style={{ flex: 1 }} />
        <button
          onClick={generateFromMealPlan}
          disabled={generating}
          style={{
            background: "var(--accent)",
            color: "var(--accent-on)",
            padding: "10px 16px",
            borderRadius: 10,
            border: "none",
            fontSize: "var(--type-body-s)",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Sparkles size={14} /> {generating ? "Generating…" : "From meal plan"}
        </button>
      </header>

      <div style={{ display: "flex", gap: 8 }}>
        <input
          value={adding}
          onChange={(e) => setAdding(e.target.value)}
          placeholder="Add item…"
          onKeyDown={(e) => {
            if (e.key === "Enter" && adding.trim()) {
              addItem.mutate(adding.trim());
              setAdding("");
            }
          }}
          style={{
            flex: 1,
            padding: 10,
            borderRadius: 8,
            border: "1px solid var(--hair-2)",
            background: "var(--bg)",
            color: "var(--fg)",
            fontSize: "var(--type-body-s)",
            fontFamily: "inherit",
          }}
        />
        <button
          onClick={() => {
            if (adding.trim()) {
              addItem.mutate(adding.trim());
              setAdding("");
            }
          }}
          style={{
            background: "var(--accent)",
            color: "var(--accent-on)",
            padding: "10px 14px",
            borderRadius: 8,
            border: "none",
            cursor: "pointer",
          }}
        >
          Add
        </button>
      </div>

      {items.isLoading ? (
        <SkeletonLoader variant="card" height={160} />
      ) : (items.data ?? []).length === 0 ? (
        <EmptyState
          headline="Empty list."
          sub="Type to add items, or generate from your meal plan."
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {sortedCats.map((cat) => (
            <section key={cat}>
              <h2
                style={{
                  margin: "0 0 6px 0",
                  fontSize: 11,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "var(--fg-3)",
                }}
              >
                {cat}
              </h2>
              <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 4 }}>
                {grouped.get(cat)!.map((i) => (
                  <li
                    key={i.id}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "auto 1fr auto",
                      gap: 12,
                      alignItems: "center",
                      padding: "10px 12px",
                      border: "1px solid var(--hair)",
                      borderRadius: 8,
                      background: "var(--bg-2)",
                      opacity: i.done ? 0.5 : 1,
                      textDecoration: i.done ? "line-through" : "none",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={i.done}
                      onChange={() => toggleItem.mutate({ id: i.id, done: !i.done })}
                    />
                    <div>
                      <span style={{ fontSize: "var(--type-body-s)" }}>{i.raw_text}</span>
                      {i.preferred_store && (
                        <span style={{ marginLeft: 6, fontSize: 11, color: "var(--fg-3)" }}>
                          @ {i.preferred_store}
                        </span>
                      )}
                    </div>
                    {i.estimated_cost != null && (
                      <span className="mono" style={{ fontSize: 12, color: "var(--fg-3)" }}>
                        {formatMoney(i.estimated_cost, i.estimated_cost_currency ?? "CHF")}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
